import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, Cloud, Boxes, Building2 } from "lucide-react";

const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });
import Footer from "../components/Footer";

const TC_URL = "https://tomorrowcentral.com";
const VIOLET = "#7c3aed";

const WHAT = [
  {
    Icon: Cloud,
    title: "Free, self-serve tools",
    desc: "Point-and-run tools like Cloud Cost Sentinel — sign in, connect a scoped role you can revoke anytime, and get evidence-backed results. Many are free forever.",
  },
  {
    Icon: Boxes,
    title: "Two front doors",
    desc: "Every tool is a console your team can click and an MCP server your agents can call — same auth, same results. Built for people and agents.",
  },
  {
    Icon: Building2,
    title: "Part of the itmtb ecosystem",
    desc: "Tomorrow Central is how itmtb ships free, self-serve tools. When a tool isn't enough, the same team builds custom software for you.",
  },
];

export default function TomorrowCentralPage() {
  return (
    <div className="bg-white">
      <Head>
        <title>Tomorrow Central — self-serve tools for infra, tech &amp; agents | itmtb</title>
        <meta
          name="description"
          content="Tomorrow Central is itmtb's self-serve tools platform — free tools for infra, tech and agents, where every tool ships a UI for people and an MCP server for agents."
        />
        <link rel="canonical" href="https://itmtb.com/tomorrow-central" />
      </Head>

      <Navbar />

      {/* Hero — itmtb teal */}
      <section className="bg-[#1B6B93]">
        <div className="mx-auto max-w-[1600px] w-[90%] md:w-[85%] pt-[110px] pb-[70px] md:pt-[130px] md:pb-[90px]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-4">
              itmtb · Product
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5">
              Tomorrow Central
            </h1>
            <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl mb-9">
              The tech stack for agents and people — self-serve tools for infra, tech and agents.
              Free to start, with a UI your team can click and an MCP server your agents can call.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={TC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: VIOLET }}
              >
                Explore the tools <ArrowRight size={16} />
              </a>
              <a
                href={`${TC_URL}/developers`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                For agents (MCP)
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What it is */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] w-[90%] md:w-[85%] py-16 md:py-24">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3">
            What Tomorrow Central is
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mb-12">
            A growing catalogue of focused tools you can run yourself — no infrastructure to manage.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHAT.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-7 hover:border-[#1B6B93]/40 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#1B6B93]/10 flex items-center justify-center mb-5">
                  <Icon size={24} className="text-[#1B6B93]" strokeWidth={1.75} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* itmtb tie + CTA */}
      <section className="bg-slate-50 border-t border-slate-100">
        <div className="mx-auto max-w-[1600px] w-[90%] md:w-[85%] py-16 md:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-3">
                From the team at itmtb.
              </h2>
              <p className="text-slate-500 text-lg leading-relaxed">
                Tomorrow Central is where itmtb ships free, self-serve tools. If you need more than a
                tool — a private deployment, an integration, or software built for your business —
                itmtb builds it.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a
                href={TC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: VIOLET }}
              >
                Explore Tomorrow Central <ArrowRight size={16} />
              </a>
              <Link href="/contact-us">
                <a className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400">
                  Talk to itmtb
                </a>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
